import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  Trash2,
  Plus,
  ExternalLink,
  AlertTriangle,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  TrendingUp,
  Users,
  Clock
} from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';

interface ConnectedAccount {
  id: string;
  platform: string;
  account_name: string;
  username: string;
  profile_image?: string;
  followers: number;
  following?: number;
  posts_count?: number;
  is_connected: boolean;
  last_sync?: string;
  token_expires?: string;
  token_expires_at?: string; // Backend field name
  permissions?: string[];
}

interface AccountsManagerProps {
  accounts?: ConnectedAccount[];
  loading?: boolean;
  onConnect?: (platform: string) => void;
  onDisconnect?: (accountId: string) => void;
  onRefresh?: (accountId: string) => void;
}

// Custom icons for platforms without Lucide icons
const TikTokIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const ThreadsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
  </svg>
);

const YoutubeShortsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z"/>
  </svg>
);

const platformConfig: Record<string, {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  gradient: string;
  descriptionKey: string;
}> = {
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'text-pink-600 dark:text-pink-400',
    gradient: 'from-purple-500 to-pink-500',
    descriptionKey: 'instagram'
  },
  facebook: {
    name: 'Facebook',
    icon: Facebook,
    color: 'text-blue-600 dark:text-blue-400',
    gradient: 'from-blue-600 to-blue-500',
    descriptionKey: 'facebook'
  },
  twitter: {
    name: 'X (Twitter)',
    icon: Twitter,
    color: 'text-gray-900 dark:text-white',
    gradient: 'from-gray-900 to-gray-700',
    descriptionKey: 'twitter'
  },
  linkedin: {
    name: 'LinkedIn',
    icon: Linkedin,
    color: 'text-blue-700 dark:text-blue-400',
    gradient: 'from-blue-700 to-blue-600',
    descriptionKey: 'linkedin'
  },
  youtube: {
    name: 'YouTube',
    icon: Youtube,
    color: 'text-red-600 dark:text-red-400',
    gradient: 'from-red-600 to-red-500',
    descriptionKey: 'youtube'
  },
  youtube_shorts: {
    name: 'YouTube Shorts',
    icon: YoutubeShortsIcon,
    color: 'text-red-500 dark:text-red-400',
    gradient: 'from-red-500 to-orange-500',
    descriptionKey: 'youtube_shorts'
  },
  tiktok: {
    name: 'TikTok',
    icon: TikTokIcon,
    color: 'text-gray-900 dark:text-white',
    gradient: 'from-gray-900 to-pink-500',
    descriptionKey: 'tiktok'
  },
  threads: {
    name: 'Threads',
    icon: ThreadsIcon,
    color: 'text-gray-900 dark:text-white',
    gradient: 'from-gray-900 to-gray-600',
    descriptionKey: 'threads'
  },
  facebook_ads: {
    // Separate connection from social Facebook: needs ads_management/ads_read
    // scopes which regular FB login doesn't include. Without this connection,
    // the "Impulsionar Post" rocket icon stays hidden on every post card.
    name: 'Meta Ads (Facebook)',
    icon: Facebook,
    color: 'text-blue-700 dark:text-blue-400',
    gradient: 'from-indigo-600 via-blue-600 to-blue-500',
    descriptionKey: 'facebook_ads'
  }
};

// Platforms that users can connect - youtube_shorts is NOT listed because it uses YouTube's connection
const availablePlatforms = ['instagram', 'facebook', 'twitter', 'linkedin', 'youtube', 'tiktok', 'threads', 'facebook_ads'];

// Platforms that share authentication (e.g., youtube_shorts uses youtube)
const linkedPlatforms: Record<string, string> = {
  youtube_shorts: 'youtube'
};

const AccountsManager: React.FC<AccountsManagerProps> = ({
  accounts = [],
  loading = false,
  onConnect,
  onDisconnect,
  onRefresh
}) => {
  const { t } = useTranslation();
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const connectedAccounts = accounts.filter(a => a.is_connected);

  // Check if a platform is connected (directly or via linked platform)
  const isPlatformConnected = (platform: string): boolean => {
    const directConnection = accounts.some(a => a.platform === platform && a.is_connected);
    if (directConnection) return true;

    // Check if this platform is linked to another (e.g., youtube_shorts -> youtube)
    const linkedTo = linkedPlatforms[platform];
    if (linkedTo) {
      return accounts.some(a => a.platform === linkedTo && a.is_connected);
    }

    return false;
  };

  const disconnectedPlatforms = availablePlatforms.filter(p => !isPlatformConnected(p));

  const handleRefresh = async (accountId: string) => {
    setRefreshingId(accountId);
    await onRefresh?.(accountId);
    setTimeout(() => setRefreshingId(null), 1000);
  };

  const formatLastSync = (dateString?: string): string => {
    if (!dateString) return t('common.never', 'Never');
    const date = new Date(dateString);
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffMinutes < 1) return t('common.now', 'Now');
    if (diffMinutes < 60) return t('common.minutesAgo', '{{count}} min ago').replace('{{count}}', String(diffMinutes));
    if (diffMinutes < 1440) return t('common.hoursAgo', '{{count}}h ago').replace('{{count}}', String(Math.floor(diffMinutes / 60)));
    return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const isTokenExpiringSoon = (expiresDate?: string): boolean => {
    if (!expiresDate) return false;
    const expires = new Date(expiresDate);
    // Check if the date is valid
    if (isNaN(expires.getTime())) return false;
    const now = new Date();
    const daysUntilExpiry = (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry < 7;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connected Accounts */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('social.connectedAccounts', 'Contas Conectadas')} ({connectedAccounts.length})
        </h3>

        {connectedAccounts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('social.noConnectedAccounts', 'Nenhuma conta conectada')}
              </h4>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('social.connectAccountsDesc', 'Conecte suas redes sociais para começar a publicar')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {connectedAccounts.map((account) => {
              const config = platformConfig[account.platform];
              const Icon = config?.icon;
              const isExpiringSoon = isTokenExpiringSoon(account.token_expires || account.token_expires_at);

              return (
                <Card key={account.id} className="overflow-hidden">
                  <div className={`h-2 bg-gradient-to-r ${config?.gradient}`} />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${config?.gradient} text-white`}>
                          {Icon && <Icon className="h-6 w-6" />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {account.account_name || account.username || config?.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            @{account.username?.replace('@', '') || account.platform}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      </div>
                    </div>

                    {/* Stats - LinkedIn API doesn't provide these metrics */}
                    {account.platform !== 'linkedin' && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-4">
                        {account.followers > 0 && (
                          <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatNumber(account.followers)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('social.followers', 'Followers')}</p>
                          </div>
                        )}
                        {account.following !== undefined && account.following > 0 && (
                          <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatNumber(account.following)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('social.following', 'Following')}</p>
                          </div>
                        )}
                        {account.posts_count !== undefined && account.posts_count > 0 && (
                          <div className="text-center p-2 rounded-lg bg-gray-50 dark:bg-gray-900/50">
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {formatNumber(account.posts_count)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('social.posts', 'Posts')}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Token Warning */}
                    {isExpiringSoon && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 mb-4">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm">Token expira em breve - reconecte</span>
                      </div>
                    )}

                    {/* Last Sync & Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t('social.synced', 'Synced')} {formatLastSync(account.last_sync)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleRefresh(account.id)}
                          disabled={refreshingId === account.id}
                          className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          title={t('common.refresh', 'Sync')}
                        >
                          <RefreshCw className={`h-4 w-4 ${refreshingId === account.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => onDisconnect?.(account.id)}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title={t('social.disconnectAccount', 'Disconnect')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Available Platforms */}
      {disconnectedPlatforms.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {t('social.availablePlatforms', 'Conectar Nova Conta')}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {disconnectedPlatforms.map((platform) => {
              const config = platformConfig[platform];
              const Icon = config?.icon;

              return (
                <button
                  key={platform}
                  onClick={() => onConnect?.(platform)}
                  className="flex items-center gap-4 p-4 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all text-left group"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${config?.color} group-hover:bg-gradient-to-br group-hover:${config?.gradient} group-hover:text-white transition-all`}>
                    {Icon && <Icon className="h-6 w-6" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {config?.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {config?.descriptionKey ? t(`social.platformDescriptions.${config.descriptionKey}`, config.name) : ''}
                    </p>
                  </div>
                  <Plus className="h-5 w-5 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
            <Settings className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100">
              {t('social.permissionsInfo', 'Sobre Permissões')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {t('social.permissionsInfoText', 'Ao conectar suas contas, solicitamos apenas as permissões necessárias para publicar posts e acessar métricas. Seus dados estão seguros e você pode desconectar a qualquer momento.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountsManager;
